"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { THEME_PRESET_MAP, DEFAULT_THEME_KEY } from "@/lib/themes/presets";
import type { ThemePreset } from "@/lib/themes/presets";
import { FONT_OPTION_MAP, getGoogleFontUrl } from "@/lib/themes/fonts";
import { FALLBACK_LAYOUT, LayoutId, toLayoutId } from "@/lib/layouts";
import { parseHomeModules, serializeHomeModules } from "@/lib/home-modules";
import {
  Density,
  FALLBACK_DENSITY,
  FALLBACK_NAV_MODE,
  FALLBACK_POST_STYLE,
  NavMode,
  PostStyle,
  toDensity,
  toNavMode,
  toPostStyle,
} from "@/lib/layout-axes";

/** localStorage keys — only written after a successful save, not during preview */
const THEME_STORAGE_KEY = "trackstack-theme-saved";
const COLOR_MODE_STORAGE_KEY = "trackstack-color-mode-saved";

type ColorMode = "light" | "dark" | "system";

interface AppearanceState {
  colorMode: ColorMode;
  themePreset: string;
  themeFont: string;
  layout: LayoutId;
  nav: NavMode;
  postStyle: PostStyle;
  density: Density;
  /** Enabled home modules in order, comma-separated (Phase 5). */
  homeModules: string;
}

interface ThemeContextValue {
  /** Current appearance (may be unsaved local preview) */
  colorMode: ColorMode;
  themePreset: string;
  themeFont: string;
  layout: LayoutId;
  nav: NavMode;
  postStyle: PostStyle;
  density: Density;
  homeModules: string;
  /** Whether the resolved mode is dark (accounts for system preference) */
  isDark: boolean;
  /** Whether there are unsaved local changes */
  hasUnsavedChanges: boolean;
  /** Set local preview values (does NOT persist) */
  setLocalColorMode: (mode: ColorMode) => void;
  setLocalThemePreset: (preset: string) => void;
  setLocalFont: (font: string) => void;
  setLocalLayout: (layout: LayoutId) => void;
  setLocalNav: (nav: NavMode) => void;
  setLocalPostStyle: (postStyle: PostStyle) => void;
  setLocalDensity: (density: Density) => void;
  setLocalHomeModules: (homeModules: string) => void;
  /** Reset to the last persisted state */
  resetToSaved: () => void;
  /** Update the saved baseline (call after successful API save) */
  markAsSaved: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialColorMode?: string;
  initialThemePreset?: string;
  initialFont?: string;
  initialLayout?: string;
  initialNav?: string;
  initialPostStyle?: string;
  initialDensity?: string;
  initialHomeModules?: string;
}

export function ThemeProvider({
  children,
  initialColorMode = "system",
  initialThemePreset = DEFAULT_THEME_KEY,
  initialFont = "default",
  initialLayout = FALLBACK_LAYOUT,
  initialNav = FALLBACK_NAV_MODE,
  initialPostStyle = FALLBACK_POST_STYLE,
  initialDensity = FALLBACK_DENSITY,
  initialHomeModules,
}: ThemeProviderProps): React.ReactElement {
  // Saved state (from DB)
  const [saved, setSaved] = useState<AppearanceState>({
    colorMode: (initialColorMode as ColorMode) || "system",
    themePreset: initialThemePreset || DEFAULT_THEME_KEY,
    themeFont: initialFont || "default",
    layout: toLayoutId(initialLayout),
    nav: toNavMode(initialNav),
    postStyle: toPostStyle(initialPostStyle),
    density: toDensity(initialDensity),
    homeModules: serializeHomeModules(parseHomeModules(initialHomeModules)),
  });

  // Local preview state
  const [local, setLocal] = useState<AppearanceState>(saved);

  // Track system preference
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // Ref to loaded font link to avoid duplicates
  const loadedFontRef = useRef<string | null>(null);

  // Resolve whether dark mode is active
  const isDark = useMemo(() => {
    if (local.colorMode === "dark") return true;
    if (local.colorMode === "light") return false;
    return systemPrefersDark;
  }, [local.colorMode, systemPrefersDark]);

  const hasUnsavedChanges = useMemo(
    () =>
      local.colorMode !== saved.colorMode ||
      local.themePreset !== saved.themePreset ||
      local.themeFont !== saved.themeFont ||
      local.layout !== saved.layout ||
      local.nav !== saved.nav ||
      local.postStyle !== saved.postStyle ||
      local.density !== saved.density ||
      local.homeModules !== saved.homeModules,
    [local, saved],
  );

  // Listen for system color scheme changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemPrefersDark(mq.matches);

    const handler = (e: MediaQueryListEvent): void => {
      setSystemPrefersDark(e.matches);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Apply dark class to <html> — no localStorage write here (only on save)
  useEffect(() => {
    const html = document.documentElement;
    if (isDark) {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
  }, [isDark]);

  // Mirror density onto <html> so the Customise sheet previews it live, same
  // as colour mode and theme above. main-layout.tsx still sets data-density
  // on the shell root for the server-rendered (post-refresh) value — CSS only
  // requires SOME ancestor to carry the attribute, so this instant client-side
  // copy takes effect immediately during editing without touching globals.css.
  useEffect(() => {
    document.documentElement.setAttribute("data-density", local.density);
  }, [local.density]);

  // Apply theme CSS variables to <html>
  useEffect(() => {
    const preset = THEME_PRESET_MAP[local.themePreset] as
      ThemePreset | undefined;
    if (!preset) return;

    const vars = isDark ? preset.dark : preset.light;
    const html = document.documentElement;

    for (const [key, value] of Object.entries(vars)) {
      html.style.setProperty(key, value);
    }
  }, [local.themePreset, isDark]);

  // Apply font
  useEffect(() => {
    const fontOption = FONT_OPTION_MAP[local.themeFont];
    const html = document.documentElement;

    if (!fontOption || local.themeFont === "default") {
      html.style.removeProperty("--theme-font-override");
      return;
    }

    // Load Google Font if not already loaded
    const fontUrl = getGoogleFontUrl(local.themeFont);
    if (fontUrl && loadedFontRef.current !== local.themeFont) {
      const existingLink = document.querySelector(
        `link[data-theme-font="${local.themeFont}"]`,
      );
      if (!existingLink) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = fontUrl;
        link.setAttribute("data-theme-font", local.themeFont);
        document.head.appendChild(link);
      }
      loadedFontRef.current = local.themeFont;
    }

    html.style.setProperty("--theme-font-override", fontOption.cssFamily);
  }, [local.themeFont]);

  const setLocalColorMode = useCallback((mode: ColorMode) => {
    setLocal((prev) => ({ ...prev, colorMode: mode }));
  }, []);

  const setLocalThemePreset = useCallback((preset: string) => {
    setLocal((prev) => ({ ...prev, themePreset: preset }));
  }, []);

  const setLocalFont = useCallback((font: string) => {
    setLocal((prev) => ({ ...prev, themeFont: font }));
  }, []);

  const setLocalLayout = useCallback((layout: LayoutId) => {
    setLocal((prev) => ({ ...prev, layout }));
  }, []);

  const setLocalNav = useCallback((nav: NavMode) => {
    setLocal((prev) => ({ ...prev, nav }));
  }, []);

  const setLocalPostStyle = useCallback((postStyle: PostStyle) => {
    setLocal((prev) => ({ ...prev, postStyle }));
  }, []);

  const setLocalDensity = useCallback((density: Density) => {
    setLocal((prev) => ({ ...prev, density }));
  }, []);

  const setLocalHomeModules = useCallback((homeModules: string) => {
    setLocal((prev) => ({ ...prev, homeModules }));
  }, []);

  const resetToSaved = useCallback(() => {
    setLocal(saved);
  }, [saved]);

  const markAsSaved = useCallback(() => {
    setSaved(local);
    // Only persist to localStorage after a successful save — not during preview
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, local.colorMode);
      localStorage.setItem(THEME_STORAGE_KEY, local.themePreset);
    } catch {}
  }, [local]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      colorMode: local.colorMode,
      themePreset: local.themePreset,
      themeFont: local.themeFont,
      layout: local.layout,
      nav: local.nav,
      postStyle: local.postStyle,
      density: local.density,
      homeModules: local.homeModules,
      isDark,
      hasUnsavedChanges,
      setLocalColorMode,
      setLocalThemePreset,
      setLocalFont,
      setLocalLayout,
      setLocalNav,
      setLocalPostStyle,
      setLocalDensity,
      setLocalHomeModules,
      resetToSaved,
      markAsSaved,
    }),
    [
      local,
      isDark,
      hasUnsavedChanges,
      setLocalColorMode,
      setLocalThemePreset,
      setLocalFont,
      setLocalLayout,
      setLocalNav,
      setLocalPostStyle,
      setLocalDensity,
      setLocalHomeModules,
      resetToSaved,
      markAsSaved,
    ],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
